import { useEffect, useRef } from "react";

import { CbhFeatureFlag, useCbhFlag } from "../../FeatureFlags";
import { setCognitoApiTokenEnabled } from "./cognitoApiTokenState";

/**
 * Syncs the `2026-03-admin-enable-cognito-in-api-calls` LaunchDarkly flag
 * into module-level state so that non-React code (axios interceptors,
 * superagent helpers) can read the current value.
 *
 * The first sync happens during render (not in an effect) so that child
 * component effects can read the correct flag value on the initial mount.
 * Subsequent updates happen in an effect to avoid setting state during render
 * after the initial sync.
 *
 * Mount this hook once, high in the component tree (e.g. App layout).
 */
export function useSyncCognitoApiTokenFlag(): void {
  const isEnabled = useCbhFlag(CbhFeatureFlag.ADMIN_ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: false,
  });
  const hasSyncedRef = useRef(false);

  // Sync during render on first mount so the value is available before
  // child component effects run.
  if (!hasSyncedRef.current) {
    hasSyncedRef.current = true;
    setCognitoApiTokenEnabled(isEnabled);
  }

  // Sync on subsequent flag changes via effect.
  useEffect(() => {
    setCognitoApiTokenEnabled(isEnabled);
  }, [isEnabled]);
}
